import { useContext, Children, cloneElement } from 'react';
import type { ReactElement, ComponentPropsWithRef, JSX } from 'react';
import { EditableTextContext } from './EditableTextRoot';

export type EditableTextSubmitButtonProps = {
  children: ReactElement;
};

export const EditableTextSubmitButton = ({
  /**
   * A submit button, typically a [Button]() that will commit the value when clicked.
   */
  children,
}: EditableTextSubmitButtonProps): JSX.Element | null => {
  const context = useContext(EditableTextContext);
  if (!context) {
    throw new Error('EditableTextSubmitButton must be used within an EditableTextRoot context');
  }
  const { setIsEditing, value, onValueCommit, finalFocusEl, isEditing } = context;

  const handleClick = () => {
    onValueCommit?.(value);
    setIsEditing(false);
    setTimeout(() => {
      const element = finalFocusEl?.();
      if (element) {
        element.focus();
      }
    }, 0);
  };

  const onlyChild = Children.only(children);

  const triggerProps: Partial<ComponentPropsWithRef<'button'>> & {
    'data-wui-editable-text-submit'?: boolean;
  } = {
    onClick: handleClick,
    'data-wui-editable-text-submit': true,
  };

  if (!isEditing) {
    return null;
  }

  return cloneElement(onlyChild, triggerProps);
};
